import { PaymentRequiredError } from '@/errors';
import ExtraBrandChargeMixin from '@/mixins/ExtraBrandCharge.js';
import BrandService from '@/services/Brand.js';
import BrandDeactivateModalContent from '@/ui/components/BrandDeactivateModalContent.vue';

// Switching a brand on and off. The component using it can implement `onBrandStatusChanged(brand)`
// to pick up the updated brand; the company (and so its brand list) is refreshed either way.
export default {
  mixins: [ ExtraBrandChargeMixin ],
  data: () => ({
    // Brands with a running status request: their switch is replaced by a loader.
    brandStatusLoadingIds: [],
  }),
  methods: {
    isBrandStatusLoading(brand) {
      return this.brandStatusLoadingIds.includes(brand.id);
    },
    withBrandStatusLoading(brand, promise) {
      this.brandStatusLoadingIds.push(brand.id);

      return promise.finally(() => {
        this.brandStatusLoadingIds = this.brandStatusLoadingIds.filter(id => id !== brand.id);
      });
    },
    isBrandSwitchDisabled(brand) {
      return brand.is_managed
        || !!brand.deleted_at
        || this.$root.loggedInUser.isRestricted
        || this.$root.isSupportMode
        || !this.$root.isInOriginalCompany;
    },
    brandSwitchTooltip(brand) {
      if (brand.is_managed) {
        return this.tr('A brand in the Managed Account Service cannot be deactivated. Remove it from the service first.');
      }

      return null;
    },
    onChangeBrandStatus(brand, newStatus) {
      if (this.isBrandStatusLoading(brand)) {
        return;
      }

      if (newStatus) {
        this.activateBrand(brand);
      }
      else {
        this.openDeactivateBrandModal(brand);
      }
    },
    openDeactivateBrandModal(brand) {
      this.$fullscreenModal.open(
        this,
        BrandDeactivateModalContent,
        {
          brand,
          confirmCallback: pauseCampaigns => this.deactivateBrand(brand, pauseCampaigns),
        },
        // Only the Cancel button closes it, so it cannot be closed while the request runs.
        {
          classes: 'brand-deactivate-modal',
          closable: false,
          closeWithOutclick: false,
        },
      );
    },
    deactivateBrand(brand, pauseCampaigns) {
      return BrandService.deactivate(brand.id, pauseCampaigns)
        .then(result => this.handleBrandStatusResult(
          result,
          this.tr('Brand deactivated successfully.'),
          this.tr('The brand has been deactivated, but the following campaigns could not be paused on the ad platform. Please check them there.'),
        ))
        .catch(err => {
          this.error(err);
          throw err;
        });
    },
    activateBrand(brand, chargeExtraBrands = false) {
      return this.withBrandStatusLoading(brand, BrandService.activate(brand.id, chargeExtraBrands))
        .then(result => this.handleBrandStatusResult(
          result,
          this.tr('Brand reactivated successfully.'),
          this.tr('The brand has been reactivated, but the following campaigns could not be restarted on the ad platform. Please check them there.'),
        ))
        .catch(err => {
          if (!chargeExtraBrands && err instanceof PaymentRequiredError) {
            this.openExtraBrandChargeConfirmation(err, {
              title: this.tr('Reactivating brand'),
              intro: this.tr(
                'You are attempting to reactivate a brand, but your current subscription allows a maximum of :maxNumber brands.',
                { ':maxNumber': this.$root.currentCompany.max_brands },
              ),
              confirm: () => this.activateBrand(brand, true),
            });
            return;
          }

          this.error(err);
        });
    },
    async handleBrandStatusResult({ brand, failedCampaigns }, successMessage, failedCampaignsMessage) {
      await this.$store.dispatch('auth/fetch');

      this.$toast.clear();
      this.$toast.success(successMessage);

      if (failedCampaigns.length !== 0) {
        this.$errorModal.open({
          title: this.tr('Some campaigns could not be changed'),
          content: failedCampaignsMessage,
          items: failedCampaigns.map(campaign => campaign.name),
        });
      }

      if (typeof this.onBrandStatusChanged === 'function') {
        this.onBrandStatusChanged(brand);
      }

      return brand;
    },
  },
};
